import { Box, Grid, Stack } from '@mui/material';
import {
  Splide,
  SplideSlide,
  Splide as SplideComponent,
} from '@splidejs/react-splide';
import type {
  AutoplayComponent,
  Splide as SplideClass,
} from '@splidejs/splide';
import React, { useRef, useEffect, useState } from 'react';

import '@splidejs/react-splide/css';
import { SmaverseTag } from '@app/components/Product/SmaverseTag';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { theme } from '@app/theme';

interface CarouselProductDetailImageProps {
  images: string[];
  isSmaverse?: boolean;
}

export const CarouselProductDetailImage = ({
  images,
  isSmaverse = false,
}: CarouselProductDetailImageProps) => {
  const mainRef = useRef<SplideComponent | null>(null);
  const thumbContainerRef = useRef<HTMLDivElement | null>(null);
  const isHoveringRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { isMobile } = useDeviceType();

  useEffect(() => {
    const main = mainRef.current?.splide as SplideClass;
    if (!main) return;

    const onMoved = () => {
      const newIndex = main.index;
      setActiveIndex(newIndex);
      if (isHoveringRef.current) return;
      if (thumbContainerRef.current && newIndex % 2 === 0) {
        const thumbnailHeight = 90;
        thumbContainerRef.current.scrollTo({
          behavior: 'smooth',
          top: (newIndex / 2) * thumbnailHeight,
        });
      }
    };

    main.on('moved', onMoved);
    return () => {
      main.off('moved');
    };
  }, []);

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    const main = mainRef.current?.splide as SplideClass;
    main.options = { ...main?.options, autoplay: false };
    (main.Components?.Autoplay as AutoplayComponent)?.pause?.();
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    const main = mainRef.current?.splide as SplideClass;
    main.options = { ...main?.options, autoplay: true };
    (main.Components?.Autoplay as AutoplayComponent)?.play?.();
  };

  return (
    <Stack flexDirection="row" gap={2} position="relative">
      <Box
        flexGrow={1}
        sx={{
          alignItems: 'center',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
        }}
        height={{ md: 500, sm: 400, xs: 270 }}
        width={{
          md: 'calc(100% - 200px)',
          sm: 'calc(100% - 150px)',
          xs: 'calc(100% - 100px)',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onBlur={handleMouseLeave}
      >
        <Splide
          options={{
            arrows: false,
            autoplay: true,
            heightRatio: 0.6,
            interval: 2000,
            pagination: false,

            rewind: true,
            type: 'fade',
          }}
          ref={mainRef}
          aria-label="Main Images"
          style={{ width: '100%' }}
        >
          {images.map((src, index) => (
            <SplideSlide key={index} style={{ width: '100%' }}>
              <Box
                height="100%"
                width="100%"
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={src}
                  alt={`Slide ${index + 1}`}
                  style={{
                    height: 'auto',
                    maxHeight: isMobile ? '400px' : '500px',
                    objectFit: 'contain',
                    width: '100%',
                  }}
                />
              </Box>
            </SplideSlide>
          ))}
        </Splide>
      </Box>
      <Box
        ref={thumbContainerRef}
        width={{ md: 200, sm: 150, xs: 100 }}
        height={{ md: 500, sm: 400, xs: 270 }}
        sx={{
          '&::-webkit-scrollbar': { display: 'none' },
          backgroundColor: 'white',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Grid container spacing={1} columns={2} sx={{ pointerEvents: 'none' }}>
          {images.map((src, index) => (
            <Grid item xs={1} key={index}>
              <Box
                component="img"
                src={src}
                alt={`Thumbnail ${index + 1}`}
                sx={{
                  '&:hover': {
                    border: `1px solid ${theme.palette.neutral.greyDark}`,
                  },
                  border:
                    activeIndex === index
                      ? `2px solid ${theme.palette.blueAccent.main}`
                      : 'none',
                  borderRadius: 1,
                  cursor: 'pointer',
                  height: { md: 80, sm: 60, xs: 40 },
                  pointerEvents: 'auto',
                  width: '100%',
                }}
                onClick={() => {
                  mainRef.current?.splide?.go(index);
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      {isSmaverse && (
        <SmaverseTag
          sx={{
            left: 12,
            top: 12,
          }}
        />
      )}
    </Stack>
  );
};
